import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalLoginPage() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [branding, setBranding] = useState(null);
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  // Fetch tenant branding from the slug
  useEffect(() => {
    if (!tenantSlug) { setBrandingLoaded(true); return; }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/company?slug=${encodeURIComponent(tenantSlug)}`);
        if (res.ok) {
          const data = await res.json();
          if (data && (data.company_name || data.company_logo_url)) {
            setBranding(data);
          }
        }
      } catch { /* use defaults */ }
      setBrandingLoaded(true);
    })();
  }, [tenantSlug]);

  const primaryColor = branding?.primary_color || '#334155';
  const secondaryColor = branding?.secondary_color || '#475569';

  function isLightColor(hex) {
    if (!hex || hex.length < 7) return false;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 180;
  }

  // Button uses primary colour; text is dark or white depending on brightness
  const btnTextColor = isLightColor(primaryColor) ? '#1a1a1a' : '#ffffff';

  // Background is always a neutral light grey
  const bgStyle = { backgroundColor: '#e2e8f0' };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Please enter your email and password');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/portal/auth/login`,
        { email: email.trim(), password, tenant_slug: tenantSlug || '' },
        { withCredentials: true }
      );
      toast.success(`Welcome back, ${data.name || 'Candidate'}!`);
      navigate(tenantSlug ? `/portal/${tenantSlug}` : '/portal');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/portal/auth/forgot-password`, {
        email: forgotEmail.trim(),
        tenant_slug: tenantSlug || '',
      });
      setForgotSent(true);
      toast.success('If that email exists, a reset link has been sent.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!brandingLoaded) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
      <div className="w-full max-w-md">
        {/* Branding header */}
        <div className="text-center mb-8">
          {branding?.company_logo_url ? (
            <div className="flex justify-center mb-4">
              <img
                src={branding.company_logo_url.startsWith('http') ? branding.company_logo_url : `${API_URL}${branding.company_logo_url}`}
                alt={branding.company_name || 'Company'}
                className="h-14 max-w-[200px] object-contain"
                data-testid="portal-login-logo"
              />
            </div>
          ) : null}
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight" data-testid="portal-login-heading">
            {branding?.company_name || 'Candidate Portal'}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {branding ? 'Log in to your candidate portal' : 'Log in to view and complete your documents'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200" data-testid="portal-login-card">
          {/* Brand colour bar */}
          <div className="h-1.5 flex">
            <div className="flex-1" style={{ backgroundColor: primaryColor }} />
            <div className="flex-1" style={{ backgroundColor: secondaryColor }} />
          </div>

          <div className="p-8">
            {!showForgot ? (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                  <input
                    data-testid="portal-login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg outline-none transition"
                    placeholder="your.email@example.com"
                    autoComplete="email"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      data-testid="portal-login-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg outline-none transition pr-11"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="portal-login-submit"
                  disabled={loading}
                  className={`w-full py-3 text-center font-semibold rounded-lg shadow-md cursor-pointer transition hover:brightness-110 select-none ${loading ? 'opacity-50 pointer-events-none' : ''}`}
                  style={{ backgroundColor: primaryColor, color: btnTextColor }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
                <div className="text-center">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => { setShowForgot(true); setForgotEmail(email); }}
                    className="text-sm hover:underline cursor-pointer text-slate-600"
                    data-testid="portal-forgot-password-link"
                  >
                    Forgot your password?
                  </span>
                </div>
              </form>
            ) : (
              <div>
                {forgotSent ? (
                  <div className="text-center py-4" data-testid="portal-forgot-sent">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-2">Check Your Email</h3>
                    <p className="text-sm text-slate-600 mb-4">If an account exists for that email, new login details have been sent.</p>
                    <button
                      onClick={() => { setShowForgot(false); setForgotSent(false); }}
                      className="text-sm text-primary hover:underline"
                    >
                      Back to login
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    <h3 className="font-semibold text-lg text-slate-800">Reset Password</h3>
                    <p className="text-sm text-slate-600">Enter your email and we&apos;ll send you new login details.</p>
                    <div>
                      <input
                        data-testid="portal-forgot-email"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg outline-none transition"
                        placeholder="your.email@example.com"
                        required
                      />
                    </div>
                    <div
                      data-testid="portal-forgot-submit"
                      role="button"
                      tabIndex={0}
                      onClick={handleForgotPassword}
                      className={`w-full py-3 text-center font-semibold rounded-lg shadow-md cursor-pointer transition hover:brightness-110 select-none ${loading ? 'opacity-50 pointer-events-none' : ''}`}
                      style={{ backgroundColor: primaryColor, color: btnTextColor }}
                    >
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </div>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setShowForgot(false)}
                        className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
                      >
                        Back to login
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by <span className="font-medium text-slate-500">HyreDocs</span>
        </p>
      </div>
    </div>
  );
}
